import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@mui/material';

import Clusters from './Clusters';
import useProcessedSearchResults from '@shared/hooks/useProcessedSearchResults';
import LoadingImage from '@shared/components/LoadingImage';
import FlightFilters from './FlightFilters';
import TabButtonsFlights from './TabButtonsFlights/TabButtonsFlights';
import FligthsDrawer from './FligthsDrawer';
import AntTabs from './AntTabs';
import useProcessedCustomSearchResults from '@shared/hooks/useProcessedCustomSearchResults';
import useWindowSize from '@shared/hooks/useWindowsSize';

import { sxStyle } from './styles';
import { getClusterId, getElem, getSafeClusterId } from './helpers';

const Search = ({
  searchResults,
  tripLegsLength,
  changeSearch,
  originalSearchType,
}) => {
  const { business_types } = useSelector(store => store.pageConfiguration);

  const screenSize = useWindowSize();
  const flightsSearchBody = useSelector(store => store.flightsSearchBody);
  const flightsSearch = useSelector(store => store.flightsSearch);
  const infoFlightsSearch = flightsSearch[flightsSearch?.currentTripType];

  const [open, setOpen] = useState(false);
  const [idDrawerSelected, setIdDrawerSelected] = useState(null);
  const [noResultsInTheFilter, setNoResultsInTheFilter] = useState(false);

  const [isFiltering, setIsFiltering] = useState(false);

  const {
    optimizedClusters,
    flightResultFiltered,
    setFlightResultFiltered,
    bestPriceByAirline,
    bestFastConvenient,
    flightsInUSD,
    duplicates,
  } = useProcessedSearchResults(searchResults);

  const {
    tabButton,
    setTabButton,
    isCustomSearch,
    flightsCustomDta,
    setFlightsCustomDta,
    optimizedCustomClusters,
    customBestPriceByAirline,
  } = useProcessedCustomSearchResults(flightsSearchBody);

  const [viewItems, setViewItems] = useState(5);
  const [isScroll, setIsScroll] = useState(false);

  const [flightsDta, setFlightsDta] = useState([]);
  const [idInScroll, setIdInScroll] = useState(false);

  const clusterRefs = useRef({});

  const handleOpenDrawer = idDrawer => {
    setIdDrawerSelected(idDrawer);
    setOpen(true);
  };

  const handleCloseDrawer = () => {
    setOpen(false);
    setIdDrawerSelected(null);
  };

  const scrollToCluster = clusterSelect => {
    const elem = getElem(clusterSelect, isCustomSearch);

    const clusterId = getSafeClusterId(getClusterId(elem), isCustomSearch);

    const node = clusterRefs.current?.[clusterId];

    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setIsScroll(false);
      setIdInScroll(null);
    } else {
      console.warn(
        '[scrollToCluster] node NOT found for clusterId:',
        clusterId
      );

      setIdInScroll(clusterId);
      setIsScroll(true);
    }

    if (!isCustomSearch) {
      const isVisible = flightsDta.some(
        f => getSafeClusterId(f.cluster_id, false) === clusterId
      );

      if (!isVisible) {
        const newFlightsDta = flightsDta.filter(
          f => getSafeClusterId(f.cluster_id, false) !== clusterId
        );

        setFlightsDta([clusterSelect, ...newFlightsDta]);
      }
    }
  };

  useEffect(() => {
    if (!idInScroll) return;

    const node = clusterRefs.current?.[idInScroll];

    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setIsScroll(false);
      setIdInScroll(null);
    }
  }, [idInScroll, flightsDta, flightsCustomDta]);

  useEffect(() => {
    setFlightsDta(flightResultFiltered.slice(0, viewItems));
  }, [flightResultFiltered, viewItems]);

  return (
    <>
      {/* spinner global (scroll) */}
      <LoadingImage site={'Spiner'} isOpen={isScroll} />

      {flightsDta.length === 0 && <LoadingImage site="Spiner" isOpen={true} />}

      {flightsDta.length > 0 && (
        <Grid container sx={sxStyle.root}>
          <FlightFilters
            key={'filters'}
            filters={searchResults.filters}
            optimizedClusters={optimizedClusters}
            optimizedCustomClusters={optimizedCustomClusters}
            flightsCustomDta={flightsCustomDta}
            flightResultFiltered={flightResultFiltered}
            setFlightResultFiltered={
              isCustomSearch ? setFlightsCustomDta : setFlightResultFiltered
            }
            isCustomSearch={isCustomSearch}
            setNoResultsInTheFilter={setNoResultsInTheFilter}
            tabButton={tabButton}
            setIsFiltering={setIsFiltering}
          />

          {noResultsInTheFilter ? (
            <Grid sx={sxStyle.noFiltersResults}>
              <h2>Lo sentimos</h2>
              <p>No encontramos resultados para esta combinación de filtros</p>
              <p>Por favor, intente nuevamente</p>
            </Grid>
          ) : (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  mt: { xs: 0, lg: '30px' },
                  mr: { xs: 0, lg: '30px' },
                  position: 'relative',
                }}
              >
                {/* spinner mientras filtran */}
                {isFiltering && <LoadingImage site="Spiner" isOpen={true} />}

                <AntTabs
                  tabButton={tabButton}
                  setTabButton={setTabButton}
                  tripLegsLength={tripLegsLength}
                  search_type={flightsSearch?.currentTripType}
                />

                <TabButtonsFlights
                  bestPriceByAirline={
                    isCustomSearch
                      ? customBestPriceByAirline
                      : bestPriceByAirline
                  }
                  bestFastConvenient={bestFastConvenient}
                  scrollToCluster={scrollToCluster}
                  isCustomSearch={isCustomSearch}
                  tabButton={tabButton}
                  flightsInUSD={flightsInUSD}
                  changeSearch={changeSearch}
                  originalSearchType={originalSearchType}
                />

                <div id="flight-results">
                  <Clusters
                    flightsDta={flightsDta}
                    flightsCustomDta={flightsCustomDta}
                    viewItems={viewItems}
                    viewOption={flightResultFiltered.length}
                    handleSeeMore={() => setViewItems(viewItems + 5)}
                    search_type={flightsSearch?.currentTripType}
                    setIsScroll={setIsScroll}
                    flightsInUSD={flightsInUSD}
                    handleOpenDrawer={handleOpenDrawer}
                    screenSize={screenSize}
                    CanSaleInUsd={searchResults.can_sale_in_usd}
                    isCustomSearch={isCustomSearch}
                    businessTypes={business_types}
                    clusterRefs={clusterRefs}
                  />
                </div>
              </Grid>

              <FligthsDrawer
                idDrawerSelected={idDrawerSelected}
                duplicates={duplicates}
                infoFlightsSearch={infoFlightsSearch}
                flightsInUSD={flightsInUSD}
                setIsScroll={setIsScroll}
                open={open}
                setOpen={setOpen}
                handleCloseDrawer={handleCloseDrawer}
                businessTypes={business_types}
              />
            </>
          )}
        </Grid>
      )}
    </>
  );
};

export default Search;
